import React from 'react'
import { ArrowDown, ArrowUp, Copy, Layers, X } from 'lucide-react'
import styles from './CombinedPromptPanel.module.css'
import { copyText } from './clipboard'
import { CombinedPromptPanelProps } from './types'

export function buildCombinedPrompt(contents: string[]): string {
  return contents.map((c) => c.trim()).filter(Boolean).join('\n\n')
}

const CombinedPromptPanel: React.FC<CombinedPromptPanelProps> = ({ parts, onJump, onMove, onRemove, onClear }) => {
  const combined = buildCombinedPrompt(parts.map((p) => p.content))

  const handleCopy = () => copyText(combined, 'Combined prompt copied')

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Combined Prompt</h3>
          <p className={styles.meta}>
            {parts.length} {parts.length === 1 ? 'part' : 'parts'} · {combined.length.toLocaleString()} chars
          </p>
        </div>
        <div className={styles.headerActions}>
          {parts.length > 0 && (
            <button type="button" className={styles.clearBtn} onClick={onClear}>Clear</button>
          )}
          <button type="button" className={styles.copyBtn} onClick={handleCopy} disabled={!combined}>
            <Copy size={14} />
            Copy all
          </button>
        </div>
      </div>

      {parts.length === 0 ? (
        <div className={styles.empty}>
          <Layers size={20} />
          <p>Check prompts in the list to combine them here. Unsaved edits are included live.</p>
        </div>
      ) : (
        <div className={styles.parts}>
          {parts.map((part, i) => (
            <section key={part.key} className={styles.part}>
              <div className={styles.partHeader}>
                <button
                  type="button"
                  className={styles.partLabel}
                  onClick={() => onJump(part.key)}
                  title="Open this prompt for editing"
                >
                  {part.label}
                  {part.isDirty && <span className={styles.dirtyDot} title="Unsaved changes" />}
                </button>
                <div className={styles.partActions}>
                  <button type="button" onClick={() => onMove(part.key, -1)} disabled={i === 0} aria-label="Move up">
                    <ArrowUp size={13} />
                  </button>
                  <button type="button" onClick={() => onMove(part.key, 1)} disabled={i === parts.length - 1} aria-label="Move down">
                    <ArrowDown size={13} />
                  </button>
                  <button type="button" onClick={() => onRemove(part.key)} aria-label="Remove from combined prompt">
                    <X size={13} />
                  </button>
                </div>
              </div>
              <pre className={styles.partContent} onClick={() => onJump(part.key)}>{part.content.trim() || '(empty)'}</pre>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

export default CombinedPromptPanel
